import { categorizeColor } from 'src/modules/designStudio/helpers/productSelection/VariationColorGroups';
import { ProductTypesByCategory } from 'src/modules/designStudio/helpers/productSelection/constants';
import type {
  EndpointData,
  EndpointProduct,
  EndpointProvider,
  EndpointVariation,
  ROOF_TAGS,
} from 'src/modules/designStudio/helpers/productSelection/endpointTypes';
import {
  ProductLabelByRoofTag,
  RoofTags,
} from 'src/modules/designStudio/helpers/productSelection/endpointTypes';
import type {
  Product,
  ProductTypeLabel,
  Provider,
  Variation,
  VariationColor,
} from 'src/modules/designStudio/helpers/productSelection/types';
import {
  AllProductTypeLabels,
  ProductCategory,
} from 'src/modules/designStudio/helpers/productSelection/types';

export const createRealProductColor = ({
  name,
  hex,
}: {
  name: string;
  hex: string;
}): VariationColor => ({
  group: categorizeColor(hex),
  hex,
  name,
});

const isProviderValid = (product: EndpointProduct): boolean =>
  !!product.label.name &&
  AllProductTypeLabels.some(label => label === product.label.value);

const isEndpointVariationValid = (variation: EndpointVariation): boolean =>
  !!variation.texture.thumbnail;

/**
 * In product catalog every roof product has the same label ROOF, for our purposes we need more
 * granularity. We need to know if the product is a shingled, tile, or metal roof. This
 * label does not exist in product catalog so instead we are using tags to encode this information.
 *
 * Each roof product will have one of the three tags:
 * - ROOF:METAL
 * - ROOF:SHINGLE
 * - ROOF:TILE
 *
 * This function finds all products with the roof tags and changes their labels. In the future when
 * these new roof labels are added we can simply remove this code and the rest of the loading process will work.
 * @param data
 */
const fillInRoofLabels = (data: EndpointData): EndpointData => {
  // for roof products replace labels wih tag, we dont actually set roof labels on products
  data.forEach(provider => {
    provider.products.forEach(product => {
      if (product.tags.length === 0) {
        return;
      }
      const roofTag = product.tags.find(tag =>
        RoofTags.some(t => t === tag.trim().replace(' ', '')),
      );

      if (roofTag) {
        const cleanedTag = roofTag.trim().replace(' ', '');
        product.label.value = ProductLabelByRoofTag[cleanedTag as ROOF_TAGS];
      }
    });
  });

  return data;
};

const createProvider = (provider: EndpointProvider): Provider => ({
  uuid: `${provider.id}`,
  name: provider.name,
});

const createVariation = (
  variation: EndpointVariation,
  product: Product,
  endpointProduct: EndpointProduct,
): Variation => {
  const pcVariation: Variation = {
    facets: {
      sizing: variation.facets.find(facet => facet.facet_type === 'sizing')
        ?.value,
      texture: variation.facets.find(facet => facet.facet_type === 'texture')
        ?.value,
      style: variation.facets.find(facet => facet.facet_type === 'style')
        ?.value,
    },
    uuid: variation.uuid,
    id: variation.id,
    name: variation.name,
    manufacturerURL: endpointProduct.manufacturer_url ?? undefined,
    textureId: variation.texture?.id,
    thumbnailURL: variation.texture.thumbnail ?? undefined,
    product,
  };

  if (variation.color.hex) {
    pcVariation.color = createRealProductColor(variation.color);
  }

  return pcVariation;
};

const createProduct = (
  product: EndpointProduct,
  provider: Provider,
): Product => {
  const pCProduct: Product = {
    uuid: product.uuid,
    id: product.id,
    name: product.name,
    type: product.label.value as ProductTypeLabel,
    provider,
    variations: [],
  };
  pCProduct.variations = product.variations
    .filter(v => isEndpointVariationValid(v))
    .map(v => createVariation(v, pCProduct, product));
  return pCProduct;
};

export class ProductDataStore {
  private products: Record<ProductTypeLabel, Product[]>;

  private providers: Map<string, string>;

  private variationsUUIDMapsByCategory:
    | Record<ProductCategory, Map<string, Variation>>
    | undefined;

  constructor(data: EndpointData) {
    const filledData = fillInRoofLabels(data);

    this.products = {};
    this.providers = new Map<string, string>();
    this.variationsUUIDMapsByCategory = undefined;

    AllProductTypeLabels.forEach(label => {
      this.products[label] = [];
    });

    filledData.forEach(provider => {
      const pcProvider = createProvider(provider);

      if (!this.providers.has(pcProvider.uuid)) {
        this.providers.set(pcProvider.uuid, pcProvider.name);
      }
      provider.products
        .filter(p => isProviderValid(p))
        .forEach(product => {
          this.products[product.label.value!].push(
            createProduct(product, pcProvider),
          );
        });
    });
  }

  getProducts(productType: ProductTypeLabel): Product[] {
    return this.products[productType];
  }

  private populateVariationUUIDMap() {
    const categories = [
      ProductCategory.WALLS,
      ProductCategory.ROOF,
      ProductCategory.TRIM,
    ];

    this.variationsUUIDMapsByCategory = {
      [ProductCategory.WALLS]: new Map<string, Variation>(),
      [ProductCategory.ROOF]: new Map<string, Variation>(),
      [ProductCategory.TRIM]: new Map<string, Variation>(),
    };

    categories.forEach(category => {
      const productTypesInCategory = ProductTypesByCategory[category];

      productTypesInCategory.forEach(productTypeLabel => {
        const products = this.products[productTypeLabel];

        products.forEach(product => {
          product.variations.forEach(variation => {
            this.variationsUUIDMapsByCategory![category].set(
              variation.uuid,
              variation,
            );
          });
        });
      });
    });
  }

  getVariationByUUID(
    uuid: string,
    category: ProductCategory,
  ): Variation | undefined {
    if (!this.variationsUUIDMapsByCategory) {
      this.populateVariationUUIDMap();
    }
    return this.variationsUUIDMapsByCategory![category].get(uuid);
  }

  getProviderNameByUUID(uuid: string): string | undefined {
    return this.providers.get(uuid);
  }

  getProductTypeFromTextureId(
    category: ProductCategory,
    textureID: number,
  ): ProductTypeLabel {
    let productType = ProductTypesByCategory[category][0];

    ProductTypesByCategory[category].forEach(type => {
      const matchingProduct = this.getProducts(type).find(product =>
        product.variations.find(variation => variation.textureId === textureID),
      );
      if (matchingProduct) {
        productType = matchingProduct.type;
      }
    });

    return productType;
  }

  private createProviderAllowListDebugString(ids: string[]) {
    return ids.map(id => ({
      id,
      name: this.getProviderNameByUUID(id) ?? 'Not found',
    }));
  }

  public removeProvidersNotInList(
    providerIds: string[],
    isPrintDebugOn: boolean,
  ) {
    if (isPrintDebugOn) {
      // eslint-disable-next-line no-console
      console.info(
        `ProductDataStore.removeProvidersNotInList()\nRemoving any provider not in list: \n ${JSON.stringify(
          this.createProviderAllowListDebugString(providerIds),
          undefined,
          2,
        )}`,
      );
    }
    AllProductTypeLabels.forEach(productLabel => {
      this.products[productLabel] = this.products[productLabel].filter(
        product => providerIds.some(id => id === product.provider.uuid),
      );
    });

    this.populateVariationUUIDMap();
  }
}

export const extractAllVariationsFromProducts = (
  products: Product[],
): Variation[] => products.flatMap(product => product.variations);

export const groupProductsByProvider = (
  products: Product[],
): { provider: Provider; products: Product[] }[] => {
  const providerMap = new Map<string, Product[]>();

  products.forEach(product => {
    const providerId = product.provider.uuid;
    const productsFromProvider = providerMap.get(providerId);
    providerMap.set(
      providerId,
      productsFromProvider ? [product, ...productsFromProvider] : [product],
    );
  });

  return Array.from(providerMap, ([_, value]) => ({
    provider: value[0].provider,
    products: value,
  }));
};
